/**
 * Client-side GeoJSON utilities for Collateral Map feature.
 * Lightweight validation and transformation helpers.
 * 
 * [COLLATERAL MAP] Added 2026-07-14
 * [G9 MULTI-POLYGON] Updated 2026-07-16 — FeatureCollection support
 */

import { COLLATERAL_MAP } from "../../../shared/constants/collateralMap";

// ============================================================
// Types
// ============================================================

/** Single polygon Feature (used during drawing) */
export type GeoJsonPolygon = {
  type: "Feature";
  geometry: {
    type: "Polygon";
    coordinates: [number, number][][];
  };
  properties: Record<string, unknown>;
};

/**
 * FeatureCollection of polygons (used for multi-polygon storage).
 * This is the canonical storage format in DB (geoBoundaryJson column).
 * A single polygon is stored as a FeatureCollection with 1 feature.
 */
export type GeoJsonFeatureCollection = {
  type: "FeatureCollection";
  features: GeoJsonPolygon[];
  properties: {
    totalAreaSqM: number;
    savedAt?: string;
    savedBy?: string | number;
    [key: string]: unknown;
  };
};

/** Union type for anything that can be stored in geoBoundaryJson */
export type GeoJsonBoundary = GeoJsonPolygon | GeoJsonFeatureCollection;

// ============================================================
// Type Guards
// ============================================================

export function isFeatureCollection(v: unknown): v is GeoJsonFeatureCollection {
  return (
    !!v &&
    typeof v === "object" &&
    (v as Record<string, unknown>).type === "FeatureCollection" &&
    Array.isArray((v as Record<string, unknown>).features)
  );
}

export function isSinglePolygon(v: unknown): v is GeoJsonPolygon {
  return (
    !!v &&
    typeof v === "object" &&
    (v as Record<string, unknown>).type === "Feature" &&
    !!(v as GeoJsonPolygon).geometry &&
    (v as GeoJsonPolygon).geometry?.type === "Polygon"
  );
}

// ============================================================
// Conversion helpers
// ============================================================

/**
 * Wrap a single GeoJsonPolygon into a FeatureCollection.
 * Used when saving a single polygon to DB.
 */
export function wrapInFeatureCollection(
  polygon: GeoJsonPolygon,
  areaSqM: number
): GeoJsonFeatureCollection {
  return {
    type: "FeatureCollection",
    features: [polygon],
    properties: {
      totalAreaSqM: Math.round(areaSqM),
      savedAt: new Date().toISOString(),
    },
  };
}

/**
 * Normalize any stored GeoJSON (old single Polygon OR new FeatureCollection)
 * into a FeatureCollection. Ensures backward compatibility.
 */
export function normalizeToFeatureCollection(
  raw: unknown
): GeoJsonFeatureCollection | null {
  if (!raw) return null;
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (isFeatureCollection(parsed)) return parsed;
    if (isSinglePolygon(parsed)) {
      // Legacy single-polygon: wrap it
      const areaSqM = Number((parsed as GeoJsonPolygon).properties?.areaSqM) || 0;
      return wrapInFeatureCollection(parsed as GeoJsonPolygon, areaSqM);
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Get all polygons from a FeatureCollection (or single polygon).
 */
export function getPolygons(boundary: GeoJsonBoundary | null): GeoJsonPolygon[] {
  if (!boundary) return [];
  if (isFeatureCollection(boundary)) return boundary.features;
  if (isSinglePolygon(boundary)) return [boundary];
  return [];
}

/**
 * Get total area (sum of all polygons) from a boundary.
 */
export function getTotalAreaSqM(boundary: GeoJsonBoundary | null): number {
  if (!boundary) return 0;
  if (isFeatureCollection(boundary)) {
    return Number(boundary.properties?.totalAreaSqM) || 0;
  }
  return Number((boundary as GeoJsonPolygon).properties?.areaSqM) || 0;
}

/**
 * Build a FeatureCollection from an array of polygons + their individual areas.
 */
export function buildFeatureCollection(
  polygons: GeoJsonPolygon[],
  areasPerPolygon: number[]
): GeoJsonFeatureCollection {
  const totalAreaSqM = areasPerPolygon.reduce((s, a) => s + a, 0);
  const features = polygons.map((p, i) => ({
    ...p,
    properties: {
      ...p.properties,
      areaSqM: Math.round(areasPerPolygon[i] ?? 0),
      polygonIndex: i,
      label: (p.properties?.label as string) || `ຜືນທີ ${i + 1}`,
      // Sprint v131: persist custom color per polygon (null = use POLYGON_COLORS[idx] default)
      color: (p.properties?.color as string) || null,
    },
  }));
  return {
    type: "FeatureCollection",
    features,
    properties: {
      totalAreaSqM: Math.round(totalAreaSqM),
      savedAt: new Date().toISOString(),
    },
  };
}

// ============================================================
// Single-polygon helpers (unchanged, for drawing phase)
// ============================================================

/**
 * Round all coordinates to N decimal places (default 6 ≈ 0.1m precision).
 */
export function roundCoordinates(
  geoJson: GeoJsonPolygon,
  decimals = 6
): GeoJsonPolygon {
  const factor = Math.pow(10, decimals);
  return {
    ...geoJson,
    geometry: {
      ...geoJson.geometry,
      coordinates: geoJson.geometry.coordinates.map((ring) =>
        ring.map(([lng, lat]) => [
          Math.round(lng * factor) / factor,
          Math.round(lat * factor) / factor,
        ] as [number, number])
      ),
    },
  };
}

/**
 * Count total vertices in a GeoJSON polygon.
 * Excludes the closing vertex (first === last).
 */
export function countVertices(geoJson: GeoJsonPolygon): number {
  if (!geoJson?.geometry?.coordinates?.[0]) return 0;
  const ring = geoJson.geometry.coordinates[0];
  return Math.max(0, ring.length - 1);
}

/**
 * Extract the outer ring coordinates as [lat, lng] pairs for Leaflet.
 * GeoJSON uses [lng, lat]; Leaflet uses [lat, lng].
 */
export function extractLatLngs(geoJson: GeoJsonPolygon): [number, number][] {
  if (!geoJson?.geometry?.coordinates?.[0]) return [];
  return geoJson.geometry.coordinates[0].map(
    ([lng, lat]) => [lat, lng] as [number, number]
  );
}

/**
 * Basic client-side validation of a GeoJSON polygon.
 */
export function validateGeoJsonPolygon(geoJson: unknown): {
  valid: boolean;
  error?: string;
} {
  if (!geoJson || typeof geoJson !== "object") {
    return { valid: false, error: "ຂໍ້ມູນ GeoJSON ບໍ່ຖືກຕ້ອງ" };
  }

  const g = geoJson as Record<string, unknown>;

  if (g.type !== "Feature") {
    return { valid: false, error: "ຕ້ອງເປັນ GeoJSON Feature" };
  }

  const geometry = g.geometry as Record<string, unknown> | undefined;
  if (!geometry || geometry.type !== "Polygon") {
    return { valid: false, error: "ຕ້ອງເປັນ Polygon geometry" };
  }

  const coords = geometry.coordinates as unknown[][] | undefined;
  if (!Array.isArray(coords) || coords.length === 0) {
    return { valid: false, error: "ບໍ່ມີ coordinates" };
  }

  const ring = coords[0] as [number, number][] | undefined;
  if (!Array.isArray(ring) || ring.length < 4) {
    return { valid: false, error: "Polygon ຕ້ອງມີຢ່າງໜ້ອຍ 3 ຈຸດ" };
  }

  const first = ring[0];
  const last = ring[ring.length - 1];
  if (first[0] !== last[0] || first[1] !== last[1]) {
    return { valid: false, error: "Polygon ring ຕ້ອງປິດ (ຈຸດທຳອິດ = ຈຸດສຸດທ້າຍ)" };
  }

  const vertexCount = ring.length - 1;
  if (vertexCount > COLLATERAL_MAP.MAX_VERTICES) {
    return {
      valid: false,
      error: `ຈຳນວນຈຸດ (${vertexCount}) ເກີນຂີດຈຳກັດ ${COLLATERAL_MAP.MAX_VERTICES} ຈຸດ`,
    };
  }

  const { minLon, maxLon, minLat, maxLat } = COLLATERAL_MAP.LAO_BOUNDS;
  for (const [lng, lat] of ring) {
    if (
      typeof lng !== "number" ||
      typeof lat !== "number" ||
      lng < minLon ||
      lng > maxLon ||
      lat < minLat ||
      lat > maxLat
    ) {
      return {
        valid: false,
        error: `ຈຸດ [${lng}, ${lat}] ຢູ່ນອກຂອບເຂດ ສປປ ລາວ`,
      };
    }
  }

  return { valid: true };
}

/**
 * Parse GeoJSON from DB — returns FeatureCollection (normalizes legacy single polygon).
 * This is the primary parse function for loading from DB.
 */
export function parseGeoJson(raw: unknown): GeoJsonFeatureCollection | null {
  return normalizeToFeatureCollection(raw);
}

/**
 * Parse GeoJSON from DB as single polygon (legacy compat — only first feature).
 * Use parseGeoJson() for new code.
 */
export function parseGeoJsonSingle(raw: unknown): GeoJsonPolygon | null {
  const fc = normalizeToFeatureCollection(raw);
  return fc?.features?.[0] ?? null;
}

/**
 * Get area from GeoJSON properties (backward compat).
 */
export function getAreaFromGeoJson(geoJson: GeoJsonBoundary | null): number {
  return getTotalAreaSqM(geoJson);
}

/**
 * Serialize FeatureCollection to string for DB storage.
 */
export function serializeFeatureCollection(
  fc: GeoJsonFeatureCollection
): string {
  return JSON.stringify(fc);
}

/**
 * Legacy: Serialize single GeoJSON polygon to string for DB storage.
 * @deprecated Use buildFeatureCollection + serializeFeatureCollection instead.
 */
export function serializeGeoJson(
  geoJson: GeoJsonPolygon,
  areaSqM: number
): string {
  const fc = wrapInFeatureCollection(roundCoordinates(geoJson), areaSqM);
  return JSON.stringify(fc);
}
